import { useMemo } from 'react';
import { useI18n } from '../../../../shared/i18n/useI18n.js'

import { formatProductPrice } from '../../../../shared/utils/index.js'

import { makeGetRowState } from '../../utils/unitTableSettings.js'
import { DATA_ITEMS_IDS, ACTION_IDS }   from '../../constants/catalogDataDisplay.js';
import { renderActionButtons } from '../ProductActions/renderActionButtons.js'
import { CARD_UI_MAP } from './cardDataUiRegistry.jsx'

import { DataItem }            from '../../../../shared/ui/DataItem/index.js'
import { HeartButton }         from '../../../../shared/ui/HeartButton/HeartButton.jsx'
import { PriceBlock }          from '../../../../shared/ui/PriceBlock/index.js'
import { StatusBadge }         from '../../../../shared/ui/StatusBadge/index.js'

import styles from './ProductCards.module.scss'

/**
 * ProductCards — mobile-optimised card stack of product items.
 *
 * Designed for narrow viewports. Cards stack vertically, each stretches
 * horizontally to fill the container width. The entire card row is clickable.
 *
 * @param {object}    props
 * @param {Array}     props.filteredProducts              - Pre-sorted, pre-filtered product array
 * @param {{ pricesVisible?: boolean, pricesSoldVisible?: boolean, pricesReservedVisible?: boolean }} [props.pricesVisibility]
 * @param {string|null} [props.activeProductKey]  - Active product key (id_cad or uuid)
 * @param {Function} [props.onProductClick]       - (product) => void
 * @param {Function} [props.isFavorite]           - (uuid: string) => boolean
 * @param {Function} [props.toggleFavorite]       - (uuid: string) => void
 */
export function ProductCards({
  activeActions     = [],
  activeProductKey  = null,
  cardDataItems     = [],
  filteredProducts  = [],
  isFavorite,
  noDataText,
  onProductClick,
  pricesVisibility  = {},
  reservedUnitsAccessible,
  soldUnitsAccessible,
  tiers,
  toggleFavorite,
}) {
  // ── i18n ──────────────────────────────────────────────────────────────────
  const { t } = useI18n()

  if (filteredProducts.length === 0) {
    return <div className={styles.noData}>{noDataText}</div>
  }

  const { pricesVisible } = pricesVisibility

  const { gridDataItems, hasActionsOrInquiry, orderedActionIds } = useMemo(() => {
    const gridItems = cardDataItems.filter(
      (i) => i.id !== DATA_ITEMS_IDS.ACTIONS && i.id !== DATA_ITEMS_IDS.INQUIRY
    );
    
    const hasActions = cardDataItems.some(
      (i) => i.id === DATA_ITEMS_IDS.ACTIONS || i.id === DATA_ITEMS_IDS.INQUIRY
    );

    const orderedIds = cardDataItems.flatMap((item) => {
      if (item.id === DATA_ITEMS_IDS.INQUIRY) return item.id;
      if (item.id === DATA_ITEMS_IDS.ACTIONS) return activeActions.map(a => a.id);
      return [];
    });

    return { gridDataItems: gridItems, hasActionsOrInquiry: hasActions, orderedActionIds: orderedIds };
  }, [cardDataItems, activeActions]);

  const getCardState = makeGetRowState({
    reservedUnitsAccessible,
    soldUnitsAccessible
  })

  const actionButtonsRenderContext = useMemo(() => ({
    buttonClass: '',
    size: "large",
    variant: "ghost",
    cta: false,
    isFavorite,
    toggleFavorite,
  }), [
    isFavorite,
    toggleFavorite,
  ])

  return (
    <div className={styles.stack}>
      {filteredProducts.map(p => {
        const key       = p.uuid ?? p.id_cad
        const isActive  = p.id_cad === activeProductKey || p.uuid === activeProductKey
        const cardState = getCardState(p)

        const { 
          displayPrice, 
          isHidden, 
          isOnOffer, 
          strikethroughPrice 
        } = formatProductPrice(p, pricesVisibility)

        const cardClass = [
          styles.card,
          isActive                       ? styles.cardActive        : '',
          cardState === 'inaccessible'   ? styles.cardInaccessible  : '',
          cardState === 'sold'           ? styles.cardSold          : '',
          cardState === 'reserved'       ? styles.cardReserved      : '',
        ].filter(Boolean).join(' ')

        const handleClick = cardState !== 'inaccessible' ? () => onProductClick?.(p) : undefined

        return (
          <div
            key={key}
            className={cardClass}
            onClick={handleClick}
            role="button"
            tabIndex={cardState !== 'inaccessible' ? 0 : -1}
            // Enter / Space keyboard activation
            onKeyDown={handleClick ? e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick() } } : undefined}
          >
            <div className={styles.dataGrid}>
              {gridDataItems.map(item => {
                const uiDef = CARD_UI_MAP[item.id]
                const cellClass = uiDef?.cellClass ? styles[uiDef.cellClass] : ''
                const renderContext = { 
                  ...pricesVisibility,
                  priceBlockClass: styles.cellPrice,
                  priceHiddenClass: styles.cellPriceHidden,
                }

                return (
                  <div key={item.id} className={`${styles.cell} ${cellClass}`}>
                    {typeof uiDef?.render === 'function' ? (
                      uiDef.render(p, renderContext)
                    ) : (
                      <DataItem
                        label={t(item.labelKey)}
                        value={item.renderValue ? item.renderValue(p) : (item.getValue(p, {tiers}) ?? '—')}
                      />
                    )}
                  </div>
                )
              })}
            </div>

            {/* ── actions — wrapping column, right-aligned ── */}
            {hasActionsOrInquiry && (
              <div
                className={styles.cellActions}
                onClick={e => e.stopPropagation()}
                onKeyDown={e => e.stopPropagation()}
              >
                {renderActionButtons({
                  context: {...actionButtonsRenderContext, onProductClick: handleClick},
                  product: p,
                  orderedActionIds: orderedActionIds,
                })}             
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
